import { Component } from '@theme/component';

/**
 * Tab switching for the product tabs section.
 *
 * The section renders the first available panel already open and the rest with
 * `hidden`, so the content reads correctly before this module runs and if it
 * never runs at all. All this class adds is the switch: it moves
 * `aria-selected`, the roving `tabindex` and the `hidden` attribute, plus the
 * WAI-ARIA arrow-key navigation the tabs pattern expects.
 *
 * The tab row renders even for a single source, so there is normally one button
 * per panel. Every method still bails out early rather than assuming the refs
 * are present, because the section is empty-guarded and a merchant can leave it
 * mid-render in the theme editor.
 *
 * @extends {Component<{
 *   tabs?: HTMLButtonElement[],
 *   panels: HTMLElement[],
 * }>}
 */
class ProductTabs extends Component {
  /**
   * Opens the panel belonging to the pressed tab.
   *
   * @param {Event} event - The click event, delegated from `on:click`.
   */
  selectTab(event) {
    const index = this.#indexOf(event.target);

    if (index === -1) return;

    this.#activate(index);
  }

  /**
   * Arrow/Home/End navigation across the tab row.
   *
   * @param {KeyboardEvent} event - The keydown event, delegated from `on:keydown`.
   */
  handleKeydown(event) {
    const { tabs } = this.refs;

    if (!Array.isArray(tabs) || tabs.length < 2) return;

    const current = this.#indexOf(event.target);

    if (current === -1) return;

    const last = tabs.length - 1;
    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next);
    tabs[next]?.focus();
  }

  /**
   * Index of the tab that owns an event target, or -1 when the target is not a tab.
   *
   * @param {EventTarget | null} target - The event target.
   * @returns {number}
   */
  #indexOf(target) {
    const { tabs } = this.refs;

    if (!Array.isArray(tabs) || !(target instanceof Element)) return -1;

    const tab = target.closest('[role="tab"]');

    return tab instanceof HTMLElement ? tabs.indexOf(/** @type {HTMLButtonElement} */ (tab)) : -1;
  }

  /** @param {number} index - Index of the tab and panel to open. */
  #activate(index) {
    const { tabs, panels } = this.refs;

    if (!Array.isArray(tabs) || !Array.isArray(panels)) return;

    for (const [position, tab] of tabs.entries()) {
      const selected = position === index;

      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.classList.toggle('product-tabs__tab--active', selected);
    }

    for (const [position, panel] of panels.entries()) {
      const selected = position === index;

      panel.classList.toggle('product-tabs__panel--active', selected);
      panel.toggleAttribute('hidden', !selected);
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
